/**
 * Aisle matching: pure text-to-aisle resolution.
 *
 * Takes typed text and a dictionary, returns an aisle or null. No DOM, no
 * imports; the caller (`aisles.ts`) supplies the dictionary and decides what
 * null becomes.
 *
 * Rewritten for the public-data dictionary (docs/recipe-import-plan, package
 * A). The first version scanned a 969-term seed linearly and, when the whole
 * phrase missed, let *any* single word with a prefix match decide — so
 * `Bengay cream` filed under Dairy (cream), `banana peppers` under Meat
 * (pepper → pepperoni) and `liquid egg whites` under Household (white).
 * Against 30,000 terms that fallback would be wrong far more often than
 * right, so the search is now:
 *
 *   1. the whole line, exact;
 *   2. every sub-phrase, longest first, the one ending at the head word
 *      first among equals (`roasted red peppers` → `red peppers` before
 *      `roasted red`), each tried with its head word in every plural and
 *      singular form the dictionary happens to hold;
 *   3. fuzzy, on the head word alone, six letters or more, one edit up to
 *      seven letters and two from eight, first letter fixed.
 *
 * A single word never decides on its own if it is an article, a unit or a
 * descriptor (`STOP`), and never if it is under three letters. `null` means
 * the dictionary has nothing to say; the caller asks the Worker.
 */
export type Dict = ReadonlyArray<readonly [term: string, aisle: string]>

/** Normalised term to aisle, one person's view of one table. A `Map`, not an
 *  object literal: terms are typed by people, and `map['constructor']` on a
 *  plain object answers out of `Object.prototype` (CLAUDE.md's alias-map
 *  bug). */
export type AisleMap = ReadonlyMap<string, string>

/** Rows from either aisle table as a lookup. `term` is already the
 *  normalised key both tables store, so it is not normalised again. */
export function toAisleMap(rows: ReadonlyArray<{ term: string; aisle: string }>): AisleMap {
  return new Map(rows.map(r => [r.term, r.aisle] as const))
}

/**
 * U4.1: the aisle someone has *decided* for a term, exact on its normalised
 * form. The person's own override (`grocery_aisle_overrides`) wins over the
 * shared vocabulary (`grocery_aisles`: the Worker's learned rows and an
 * admin's corrections). `undefined` means neither table has the term, which
 * is not "Other" — the dictionary and the fuzzy tiers are still to ask
 * (`aisleOf`), and a lookup for the Worker (`needsLookup`) is a separate
 * question again.
 */
export function aisleFor(term: string, shared: AisleMap, mine: AisleMap): string | undefined {
  const key = normalise(term)
  return mine.get(key) ?? shared.get(key)
}

/**
 * The `known` dictionary `aisleOf` and `needsLookup` take: the person's own
 * rows first, then every shared row they did not override. `Dict` is
 * ordered and the index keeps the first entry for a key, so for one exact
 * term this is `aisleFor`'s precedence expressed in the only shape the
 * matcher accepts; dropping the shadowed shared rows rather than relying
 * on order alone keeps it true if that ever changes.
 */
export function mergeKnown(shared: Dict, mine: Dict): Dict {
  const own = new Set(mine.map(([t]) => t))
  return [...mine, ...shared.filter(([t]) => !own.has(t))]
}

/** Which tables a correction writes. A person's override is always theirs;
 *  an admin's correction is also the vocabulary. Not the two-jobs bug: the
 *  override is the user's record of what they decided, the shared row is
 *  what everyone else's list starts from, and an admin's single action
 *  means both on purpose. `undefined` (admin status not read yet) is a
 *  person: the shared write would be refused by RLS anyway. */
export function aisleWriteTargets(isAdmin: boolean | undefined): ReadonlyArray<'override' | 'shared'> {
  return isAdmin === true ? ['override', 'shared'] : ['override']
}

export type Via = 'exact' | 'form' | 'head' | 'fuzzy'

/** A word at the front of a line that says *where the thing is kept*, and
 *  so beats whatever the thing is: `canned whole tomatoes` is Pantry however
 *  the dictionary files tomatoes, `frozen spinach leaves` is Frozen. Applied
 *  after the whole line has missed and before any sub-phrase is tried — the
 *  QA paste of 10 Oct 2026 had `1 can whole tomatoes` under Produce because
 *  `whole tomatoes` was the longest phrase the dictionary knew. */
export const FORM_WORDS: ReadonlyMap<string, string> = new Map([
  ['frozen', 'Frozen'], ['tiefkuehl', 'Frozen'], ['tiefgekuehlt', 'Frozen'], ['tk', 'Frozen'],
  ['canned', 'Pantry'], ['can', 'Pantry'], ['tinned', 'Pantry'], ['tin', 'Pantry'], ['jarred', 'Pantry'], ['jar', 'Pantry'],
  ['pickled', 'Pantry'], ['dried', 'Pantry'], ['dry', 'Pantry'], ['instant', 'Pantry'],
  ['dose', 'Pantry'], ['dosen', 'Pantry'], ['glas', 'Pantry'], ['getrocknet', 'Pantry'], ['getrocknete', 'Pantry'], ['eingelegt', 'Pantry'], ['eingelegte', 'Pantry'],
])
export interface Match { aisle: string; via: Via; term: string }

/**
 * Normalize text for matching.
 *
 * Lowercase; ä ö ü ß → ae oe ue ss; strip quantities and units at either end
 * (2x, 500g, 1 kg, 6 pack, a bunch of); collapse whitespace; strip only a
 * trailing English `s` when the word is 4+ letters (WP 8.7b — `es` and a
 * bare German `n`/`en` are deliberately not stripped; see the comment on
 * the strip itself below for why: neither reliably marks a plural, and
 * guessing wrong is a silent, permanent misclassification the fuzzy tier
 * would have caught instead).
 *
 * **Byte-identical to `src/aisle.ts`'s copy** — `test/aislematch-agree.test.ts`
 * diffs the two bodies. `grocery_aisles.term` is this function's output on
 * both sides.
 */
export function normalise(text: string): string {
  let s = text
    .toLowerCase()
    .replace(/[äöüß]/g, c => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[c]!))
  // Strip quantities/units: `2x`, `500g`, `1 kg`, `6 pack`, `a bunch of` at start
  s = s.replace(/^\s*(\d+x|\d+\s*[a-z]+|\d+|a\s+\w+\s+of)\s+/i, '')
  // Collapse whitespace
  s = s.replace(/\s+/g, ' ').trim()
  // Strip units at end: `500g`, `1kg`, `ml`, etc.
  s = s.replace(/\s+\d*\s*(?:kg|g|ml|l|pack)\s*$/, '')
  // Strip only a trailing `s`, and only on a word 4+ letters long
  // (peas → pea, eggs → egg, but bus stays bus). A first version of this
  // rule also stripped `es` and a bare German `n`, on the theory that
  // `tomatoes → tomato` and `tomaten → tomate` are both plurals worth
  // normalising away — but neither `es` nor `n` reliably marks a plural at
  // all: `apples` and `grapes` end in `es` as part of their own spelling,
  // not as a plural suffix distinct from the stem, and stripping two
  // characters turned them into `appl` and `grap` — four letters each,
  // one below the five-letter floor the fuzzy tier needs, so they reached
  // no aisle at all rather than a wrong one. `chicken`, `kitchen`, `onion`,
  // `salmon`, `lemon`, `bacon` and German `Huhn` end in a bare `n` as
  // their own singular spelling, not a plural marker, and the same rule
  // turned them into `chicke`, `kitche`, `onio`... A rule with no
  // dictionary to say *is this actually a plural* corrupts more than it
  // fixes; the one case worth stripping outright is a trailing `s`
  // (`peas` → `pea`), and the rest — `tomatoes` vs `tomato`, `tomaten` vs
  // `tomate` — is left to the fuzzy tier below, which already tolerates
  // exactly that shape of difference (one edit, well inside its distance-1
  // budget for a 5–7 letter term) without baking an irreversible guess
  // into every normalised form.
  if (s.length >= 4 && s.endsWith('s')) {
    s = s.slice(0, -1)
  }
  return s
}

/**
 * The forms a (normalised) head word might be stored under, the word itself
 * first. Nothing here is applied blindly: a form counts only if the
 * dictionary holds it, which is the rule the old `normalise` comment asked
 * for and could not have without a dictionary to ask. After `normalise` has
 * taken one `s`, `cherries` is `cherrie` and `tomatoes` is `tomatoe`, so
 * the singular forms undo that spelling; the plural forms go the other way
 * for a dictionary that stored the plural (`Tomatoes`, as the taxonomies
 * name things); the German strips cover `Tomaten` → `Tomate` and
 * `Zwiebeln` → `Zwiebel`.
 */
export function wordForms(w: string): string[] {
  const out = [w]
  // `normalise` strips one trailing `s` from the end of the *line*; a head
  // word inside a sub-phrase (`kraft singles` in `kraft singles 2%`) never
  // reached that rule, so it is applied here under the same condition —
  // and the stripped form is the one the rest of this function reasons
  // about, since that is what the dictionary keys look like.
  if (w.length >= 4 && w.endsWith('s')) {
    w = w.slice(0, -1)
    out.push(w)
  }
  if (w.length >= 5) {
    if (w.endsWith('ie')) out.push(w.slice(0, -2) + 'y')          // cherrie → cherry
    if (w.endsWith('oe')) out.push(w.slice(0, -1))                 // tomatoe → tomato
    if (/(?:ch|sh|ss|x|z)e$/.test(w)) out.push(w.slice(0, -1))     // peache → peach, boxe → box
    if (w.endsWith('y')) out.push(w.slice(0, -1) + 'ie')           // cherry → cherrie
    if (w.endsWith('o')) out.push(w + 'e')                         // tomato → tomatoe
    if (/(?:ch|sh|ss|x|z)$/.test(w)) out.push(w + 'e')             // peach → peache
    if (w.endsWith('en')) out.push(w.slice(0, -2), w.slice(0, -1)) // tomaten → tomat, tomate
    else if (w.endsWith('n')) out.push(w.slice(0, -1))             // zwiebeln → zwiebel
    if (w.endsWith('e')) out.push(w.slice(0, -1))                  // tomate → tomat (then fuzzy)
  }
  return [...new Set(out)]
}

/** Words that never decide an aisle on their own. Articles, prepositions,
 *  units, containers and the descriptors a shopping line is full of. A
 *  phrase containing one is still matched as a phrase: `trash bags` and
 *  `kitchen roll` are dictionary entries; `bag` and `roll` alone are not
 *  evidence of anything. */
export const STOP = new Set([
  'a', 'an', 'the', 'of', 'and', 'or', 'with', 'for', 'in', 'on', 'to', 'from', 'some', 'more',
  'new', 'big', 'small', 'large', 'medium', 'mini', 'jumbo', 'extra', 'fresh', 'organic', 'natural',
  'original', 'classic', 'free', 'light', 'lite', 'whole', 'mixed', 'plain', 'sliced', 'diced',
  'chopped', 'roasted', 'raw', 'cooked', 'dried', 'lean', 'low', 'fat', 'reduced', 'sweet', 'hot',
  'pack', 'packet', 'packs', 'bag', 'bags', 'box', 'jar', 'can', 'cans', 'bottle', 'bottles', 'tube',
  'roll', 'rolls', 'piece', 'pieces', 'bunch', 'kg', 'g', 'ml', 'l', 'oz', 'lb', 'lbs', 'x',
  'und', 'mit', 'ohne', 'der', 'die', 'das', 'ein', 'eine', 'stueck', 'stk', 'bund', 'packung',
])

/** Edit distance (Levenshtein): counts 1 for insertions, deletions, and substitutions. */
export function editDistance(a: string, b: string): number {
  const aLen = a.length
  const bLen = b.length
  let dp: number[] = Array(bLen + 1).fill(0).map((_, i) => i)
  for (let i = 1; i <= aLen; i++) {
    const newDp: number[] = [i]
    for (let j = 1; j <= bLen; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      newDp.push(Math.min(
        dp[j]! + 1,           // deletion
        newDp[j - 1]! + 1,    // insertion
        dp[j - 1]! + cost     // substitution
      ))
    }
    dp = newDp
  }
  return dp[bLen]!
}

interface Index {
  /** normalised term → aisle. The *first* entry for a key wins, which is
   *  how `groceryAisles.ts` makes Doug's overrides beat learned rows: it
   *  puts them first. */
  terms: Map<string, string>
  /** Single-word keys of five letters or more, by first letter, for the
   *  fuzzy tier — so a miss compares against a few hundred words, not
   *  thirty thousand. */
  fuzzyByInitial: Map<string, string[]>
  /** Per-index memo of `match` results. The grocery list calls `aisleOf`
   *  for every row on every render; a miss that reached the fuzzy tier
   *  costs a few thousand edit-distance calls and should cost them once. */
  memo: Map<string, Match | null>
}

const indexes = new WeakMap<Dict, Index>()

function indexFor(dict: Dict): Index {
  const hit = indexes.get(dict)
  if (hit) return hit
  const terms = new Map<string, string>()
  const fuzzyByInitial = new Map<string, string[]>()
  for (const [term, aisle] of dict) {
    const key = normalise(term)
    if (!key || terms.has(key)) continue
    terms.set(key, aisle)
    if (key.length >= 5 && !key.includes(' ')) {
      const list = fuzzyByInitial.get(key[0]!)
      if (list) list.push(key); else fuzzyByInitial.set(key[0]!, [key])
    }
  }
  const idx: Index = { terms, fuzzyByInitial, memo: new Map() }
  indexes.set(dict, idx)
  return idx
}

/** Every form of a phrase: the phrase with its last word in each of
 *  `wordForms`. */
function phraseForms(words: string[]): string[] {
  const head = words[words.length - 1]!
  const lead = words.slice(0, -1).join(' ')
  return wordForms(head).map(h => (lead ? `${lead} ${h}` : h))
}

/**
 * Match text against a dictionary. `null` rather than a guess.
 */
export function matchAisle(text: string, dict: Dict): Match | null {
  const norm = normalise(text)
  if (!norm) return null
  const idx = indexFor(dict)
  const memo = idx.memo.get(norm)
  if (memo !== undefined) return memo
  const result = match(norm, idx)
  if (idx.memo.size > 2000) idx.memo.clear()
  idx.memo.set(norm, result)
  return result
}

function match(norm: string, idx: Index): Match | null {
  const words = norm.split(' ')

  // 1: the whole line.
  for (const form of phraseForms(words)) {
    const aisle = idx.terms.get(form)
    if (aisle) return { aisle, via: 'exact', term: form }
  }
  // 1b: a form word at the front decides — see `FORM_WORDS`.
  if (words.length > 1) {
    const formAisle = FORM_WORDS.get(words[0]!)
    if (formAisle) return { aisle: formAisle, via: 'form', term: words[0]! }
  }
  // 2: every sub-phrase longest first; among equals the one ending at the
  // head word first.
  for (let len = words.length - 1; len >= 1; len--) {
    for (let end = words.length; end - len >= 0; end--) {
      const slice = words.slice(end - len, end)
      if (len === 1 && (slice[0]!.length < 3 || STOP.has(slice[0]!))) continue
      for (const form of phraseForms(slice)) {
        const aisle = idx.terms.get(form)
        if (aisle) return { aisle, via: 'head', term: form }
      }
    }
  }

  // 3: fuzzy, on the head word alone — six letters or more. Five was the
  // old floor, and a five-letter word one edit from a dictionary entry is
  // as likely a different word as a typo (`brand` is one edit from `bran`).
  const head = words[words.length - 1]!
  if (head.length < 6 || STOP.has(head)) return null
  const maxDist = head.length <= 7 ? 1 : 2
  let best: { term: string; dist: number } | null = null
  for (const cand of idx.fuzzyByInitial.get(head[0]!) ?? []) {
    if (Math.abs(cand.length - head.length) > maxDist) continue
    const dist = editDistance(head, cand)
    if (dist > maxDist) continue
    if (!best || dist < best.dist || (dist === best.dist && cand.length > best.term.length)) {
      best = { term: cand, dist }
    }
  }
  return best ? { aisle: idx.terms.get(best.term)!, via: 'fuzzy', term: best.term } : null
}
